import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { HttpHeaders } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../core/consts/api.const';
import { LoadingService } from '../../services/loading.service';
import { SimulationsService } from '../../services/simulations/simulations.service';
import { SnackbarService } from '../../services/snackbar.service';
import { RockImagesService } from '../../services/rock-images/rock-images.service';
import { RockModelsService } from '../../services/rock-models/rock-models.service';
import { RockThumbnailService } from '../../services/rock-thumbnail/rock-thumbnail.service';
import { UpscalingRockFile, PhaseInfo } from '../../core/models/upscaling-rock-files.model';
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
} from '../../common/popups/confirm-popup/confirm-popup.component';
import { concatMap, EMPTY, forkJoin, of, tap } from 'rxjs';
import { RemoveExtensionPipe } from '../../common/pipes/remove-extension.pipe';

export interface FluidFlowModelRow {
  id: string;
  name: string;
  direction: string;
  flowUnits: number;
  interfacialTensionMin: number;
  interfacialTensionMax: number;
  primaryDrainageMin: number;
  primaryDrainageMax: number;
  firstImbibitionMin: number;
  firstImbibitionMax: number;
  hasFirstImbibition: boolean;
}

@Component({
  selector: 'app-fluid-flow-manager',
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTableModule,
    MatChipsModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './fluid-flow-manager.component.html',
  styleUrl: './fluid-flow-manager.component.scss',
  standalone: true,
})
export class FluidFlowManagerComponent extends Destroyable(Object) implements OnInit {
  private readonly componentId: string = 'fluid-flow-manager';
  isLoading = false;

  simulationId: string = '';
  simulationName: string = '';
  rockFileName: string = '';
  rockModelName: string = '';
  simulationDirections: { direction: string; status: string }[] = [];

  thumbnailUrl: string | null = null;
  rockFile: UpscalingRockFile | null = null;
  rockModelId: string | null = null;
  phases: PhaseInfo[] = [];

  fluidFlowModels: FluidFlowModelRow[] = [];
  selectedModel: FluidFlowModelRow | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly loadingService: LoadingService,
    private readonly simulationsService: SimulationsService,
    private readonly rockImagesService: RockImagesService,
    private readonly rockModelsService: RockModelsService,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly snackbarService: SnackbarService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.route.queryParams.pipe(this.takeUntilDestroyed()).subscribe((params) => {
      this.simulationId = params['simulationId'] || '';
      this.simulationName = params['simulationName'] || '';
      this.rockFileName = params['rockFile'] || '';
      this.rockModelName = params['rockModel'] || '';

      if (this.simulationId) {
        this.loadSimulationDetails();
        this.loadFluidFlowModels();
        if (this.rockFileName) {
          this.loadThumbnail();
          this.loadRockFileMetadata();
        }
      } else {
        this.router.navigate(['/upscaling/fluid-flow-models']);
      }
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.thumbnailUrl) {
      this.rockThumbnailService.revokeThumbnailUrl(this.thumbnailUrl);
    }
  }

  onRowClick(model: FluidFlowModelRow): void {
    if (this.selectedModel?.id === model.id) {
      this.selectedModel = null;
    } else {
      this.selectedModel = model;
    }
  }

  getFileSizeDisplay(): string {
    if (!this.rockFile) return 'N/A';
    const x = this.rockFile.voxelsX || 0;
    const y = this.rockFile.voxelsY || 0;
    const z = this.rockFile.voxelsZ || 0;
    if (x && y && z) {
      return `${x}x${y}x${z}`;
    }
    return 'N/A';
  }

  formatRange(min: number, max: number): string {
    if (min === 0 && max === 0) return 'Not Available';
    return `${min.toFixed(3)} to ${max.toFixed(3)}`;
  }

  getStatusClass(status: string): string {
    if (!status) return 'status-not-started';

    switch (status.toLowerCase()) {
      case 'failure':
        return 'status-failed';
      case 'initialization':
        return 'status-initialization';
      case 'running':
        return 'status-running';
      case 'success':
      case 'completed':
        return 'status-success';
      default:
        return 'status-default';
    }
  }

  newFluidFlowModel(): void {
    this.router.navigate(['/upscaling/fluid-flow-definition'], {
      queryParams: {
        simulationId: this.simulationId,
        simulationName: this.simulationName,
        rockFile: this.rockFileName,
        rockModel: this.rockModelName,
      },
    });
  }

  viewFluidFlowModel(): void {
    if (!this.selectedModel) return;

    this.router.navigate(['/upscaling/fluid-flow-definition'], {
      queryParams: {
        simulationId: this.simulationId,
        simulationName: this.simulationName,
        rockFile: this.rockFileName,
        rockModel: this.rockModelName,
        modelId: this.selectedModel.id,
      },
    });
  }

  duplicateFluidFlowModel(): void {
    if (!this.selectedModel) return;

    this.router.navigate(['/upscaling/fluid-flow-definition'], {
      queryParams: {
        simulationId: this.simulationId,
        simulationName: this.simulationName,
        rockFile: this.rockFileName,
        rockModel: this.rockModelName,
        sourceModelId: this.selectedModel.id,
      },
    });
  }

  deleteFluidFlowModel(): void {
    if (!this.selectedModel) return;

    const dialogData: ConfirmPopupData = {
      title: 'Delete Fluid Flow Model',
      message: `Do you want to delete the fluid flow model "${this.selectedModel.name}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    };

    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      minWidth: '500px',
      maxWidth: '700px',
      minHeight: '200px',
      data: dialogData,
    });

    const modelToDelete = this.selectedModel;

    dialogRef
      .afterClosed()
      .pipe(
        concatMap((result: boolean) => {
          if (result && modelToDelete) {
            this.loadingService.startLoading(this.componentId);
            const options = {
              headers: new HttpHeaders({
                [COMPONENT_ID_HEADER]: this.componentId,
              }),
            };
            return this.simulationsService.deleteFluidFlowModel(this.simulationId, modelToDelete.id, options);
          }
          return EMPTY;
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openCustom({
            message: `Fluid flow model "${modelToDelete!.name}" deleted successfully`,
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
          this.selectedModel = null;
          this.loadFluidFlowModels();
        },
        error: () => {
          this.loadingService.stopLoading(this.componentId);
          this.snackbarService.openCustom({
            message: `Failed to delete fluid flow model "${modelToDelete!.name}"`,
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
        },
      });
  }

  getRockModelUrl(): string {
    if (!this.rockFileName || !this.rockModelId) return '#';
    const urlTree = this.router.createUrlTree(['/phase-definition'], {
      queryParams: { fileName: this.rockFileName, modelId: this.rockModelId },
    });
    return this.router.serializeUrl(urlTree);
  }

  get totalFraction(): number {
    return this.phases.reduce((sum, phase) => sum + phase.fraction, 0);
  }

  getSimulationResultsUrl(): string {
    if (!this.simulationId) return '#';
    return this.router.serializeUrl(
      this.router.createUrlTree(['/simulation-results/kabs', this.simulationId]),
    );
  }

  private loadSimulationDetails(): void {
    this.simulationsService.getSimulationById(this.simulationId).subscribe({
      next: (sim) => {
        this.simulationName = sim.name;
        this.rockFileName = sim.fileName;
        this.simulationDirections = [];
        if (sim.results) {
          sim.results.forEach((r: any) => {
            const dir = r.direction?.toUpperCase();
            if (dir) {
              this.simulationDirections.push({
                direction: dir,
                status: r.status || '—',
              });
            }
          });
        }
      },
      error: () => {
        this.snackbarService.openCustom({
          message: 'Failed to load simulation details',
          title: 'Error',
          type: 'error',
          action: 'Close',
        });
      },
    });
  }

  private loadFluidFlowModels(): void {
    this.loadingService.startLoading(this.componentId);
    this.simulationsService.getFluidFlowModels(this.simulationId).pipe(
      concatMap((models) => {
        if (!models.length) return of({ models, details: [] });
        return forkJoin(
          models.map((m) =>
            this.simulationsService.getFluidFlowModelById(this.simulationId, m.id)
          )
        ).pipe(
          concatMap((details) => of({ models, details }))
        );
      }),
      this.takeUntilDestroyed(),
    ).subscribe({
      next: ({ models, details }) => {
        this.fluidFlowModels = models.map((m, i) => {
          const detail = details[i];
          const enabledPhases = detail?.phasesDefinition?.filter(
            (p) => p.phasePorosity !== 0 && p.phasePorosity !== 1
          ) ?? [];
          const allPhasesHaveFI = enabledPhases.length > 0 &&
            enabledPhases.every(
              (p) => p.capillaryPressure?.contactAngleFirstImbibition != null &&
                     p.capillaryPressure.contactAngleFirstImbibition !== 0
            );
          return {
            id: m.id,
            name: m.name,
            direction: m.direction || '—',
            flowUnits: m.flowUnits ?? 0,
            interfacialTensionMin: m.interfactialTension?.min ?? 0,
            interfacialTensionMax: m.interfactialTension?.max ?? 0,
            primaryDrainageMin: m.primaryDrainage?.min ?? 0,
            primaryDrainageMax: m.primaryDrainage?.max ?? 0,
            firstImbibitionMin: m.firstImbibition?.min ?? 0,
            firstImbibitionMax: m.firstImbibition?.max ?? 0,
            hasFirstImbibition: allPhasesHaveFI,
          };
        });
        this.selectedModel = null;
        this.loadingService.stopLoading(this.componentId);
      },
      error: () => {
        this.loadingService.stopLoading(this.componentId);
        this.snackbarService.openCustom({
          message: 'Failed to load fluid flow models',
          title: 'Error',
          type: 'error',
          action: 'Close',
        });
      },
    });
  }

  private loadRockFileMetadata(): void {
    this.rockImagesService
      .getRockFileMetadata(this.rockFileName, 'Upscaling')
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (metadata) => {
          this.rockFile = metadata as UpscalingRockFile;
          this.processPhases();
        },
        error: () => {},
      });

    if (this.rockModelName) {
      this.rockModelsService
        .getUpscalingRockFileModels(this.rockFileName)
        .pipe(this.takeUntilDestroyed())
        .subscribe({
          next: (models) => {
            const match = models.find((m) => m.name === this.rockModelName);
            this.rockModelId = match?.id ?? null;
          },
          error: () => {},
        });
    }
  }

  private processPhases(): void {
    this.phases = [];
    if (!this.rockFile) return;
    if (this.rockFile.phases) {
      this.phases = this.rockFile.phases;
      if (this.rockFile.normalizedPhases) {
        this.phases.forEach((phase) => {
          const normalizedValue = this.rockFile!.normalizedPhases![phase.label.toString()];
          if (normalizedValue !== undefined) {
            phase.color = `rgb(${normalizedValue}, ${normalizedValue}, ${normalizedValue})`;
          } else {
            phase.color = 'rgb(128, 128, 128)';
          }
        });
      } else {
        const maxPhaseValue = this.rockFile.maxPhaseValue || 255;
        this.phases.forEach((phase) => {
          const grayValue = Math.round((phase.label / maxPhaseValue) * 255);
          phase.color = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
        });
      }
    } else if (this.rockFile.fractionHistogram) {
      this.phases = Object.entries(this.rockFile.fractionHistogram).map(([label, fraction], index) => {
        const labelNum = parseInt(label, 10);
        let grayValue = 128;
        if (this.rockFile!.normalizedPhases && this.rockFile!.normalizedPhases[label] !== undefined) {
          grayValue = this.rockFile!.normalizedPhases[label];
        } else if (this.rockFile!.maxPhaseValue) {
          grayValue = Math.round((labelNum / this.rockFile!.maxPhaseValue) * 255);
        }
        return {
          phase: index,
          label: labelNum,
          fraction: fraction,
          color: `rgb(${grayValue}, ${grayValue}, ${grayValue})`,
        };
      });
    }
  }

  private loadThumbnail(): void {
    this.rockThumbnailService.getThumbnail(this.rockFileName, 'Upscaling').subscribe({
      next: (url: string | null) => {
        this.thumbnailUrl = url;
      },
      error: () => {
        this.thumbnailUrl = null;
      },
    });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
